import { Stack, Typography, styled, useTheme } from "@mui/material";
import { CreatePlaylistRequest } from "@Shared/schemas/playlists/playlistSchemas";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { parseISO } from "date-fns";
import BybTextField from "@Components/game/common/input/text-field/byb-text-field";
import BybDatePicker from "@Components/game/common/date-picker/byb-date-picker";
import { useState } from "react";
import PlaylistSharingDialog from "./playlist-sharing-dialog";
import { UserRecord } from "@Shared/schemas/users/userSchemas";
import { TeamRecord } from "@Shared/schemas/teams/teamSchemas";
import { AccountRecord, GetAccountResponse } from "@Shared/schemas/accounts/accountSchemas";
import { BybSwitch } from "@Components/common/byb-ds";
import { useGlobalContext } from "@hooks";

// Add this interface to extend the CreatePlaylistRequest type
interface ExtendedPlaylistRequest extends Partial<CreatePlaylistRequest> {
  _userRecords?: UserRecord[];
  _teamRecords?: TeamRecord[];
  _accountRecords?: GetAccountResponse[];
}

const FieldLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.dsNeutral.v400,
  marginBottom: theme.spacing(1),
}));

interface PlaylistDetailsProps {
  details: ExtendedPlaylistRequest;
  onChange: (details: ExtendedPlaylistRequest) => void;
}

const PlaylistDetails = ({ details, onChange }: PlaylistDetailsProps) => {
  const theme = useTheme();
  const { globalState } = useGlobalContext();
  const [sharingDialogOpen, setSharingDialogOpen] = useState(false);

  const handleChange = (field: keyof CreatePlaylistRequest, value: any) => {
    onChange({ ...details, [field]: value });
  };

  const handleDateChange = (field: "start_at" | "end_at", value: Date | null) => {
    handleChange(field, value ? value.toISOString() : null);
  };

  const parseDate = (dateString: string | Date | undefined | null): Date | null => {
    if (!dateString) return null;
    
    try {
      if (dateString instanceof Date) return dateString;
      
      return parseISO(dateString.toString());
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  };

  const handleSharingSelect = (selectedUsers: UserRecord[], selectedTeams: TeamRecord[], selectedAccounts: GetAccountResponse[]) => {
    // Update all sharing fields while preserving complete records
    const newDetails = {
      ...details,
      // Store the full user/team/account objects in hidden properties
      _userRecords: selectedUsers,
      _teamRecords: selectedTeams,
      _accountRecords: selectedAccounts,
      // Store just the IDs in the properties used by the API
      shared_with_users: selectedUsers.map(user => user.id),
      shared_with_teams: selectedTeams.map(team => team.id),
      shared_with_accounts: selectedAccounts.map(account => account.id)
    };
    onChange(newDetails);
  };

  // Prepare the initial values for the sharing dialog
  // This will use full records if available, or minimal objects with just IDs otherwise
  const getInitialUsers = () => {
    // If we have full user records stored, use those
    if (details._userRecords) {
      return details._userRecords;
    }
    // Otherwise create minimal user objects from IDs
    return details.shared_with_users?.map(id => ({ id } as UserRecord)) || [];
  };

  const getInitialTeams = () => {
    // If we have full team records stored, use those
    if (details._teamRecords) {
      return details._teamRecords;
    }
    // Otherwise create minimal team objects from IDs
    return details.shared_with_teams?.map(id => ({ id } as TeamRecord)) || [];
  };

  const getInitialAccounts = () => {
    // If we have full account records stored, use those
    if (details._accountRecords) {
      return details._accountRecords;
    }
    // Otherwise create minimal account objects from IDs
    return details.shared_with_accounts?.map(id => ({ id } as GetAccountResponse)) || [];
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3}>
        {/* Name */}
        <Stack>
          <BybTextField
            label="Name"
            fullWidth
            variant="filled"
            placeholder="Enter playlist name"
            value={details.name || ""}
            hasValue={!!details.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </Stack>

        {/* Dates */}
        <Stack direction="row" spacing={2}>
          <Stack flex={1}>
            <BybDatePicker
              value={parseDate(details.start_at)}
              onChange={(date: Date | null) => handleDateChange("start_at", date)}
              label="Start date"
              renderInput={(params) => (
                <BybTextField
                  sx={{
                    '& .MuiInputBase-root': {
                      paddingRight: theme.spacing(2),
                    }
                  }}
                  {...params}
                  fullWidth
                  variant="filled"
                  placeholder="Select start date"
                  hasValue={!!details.start_at}
                />
              )}
              inputFormat="dd.MM.yyyy"
            />
          </Stack>
          <Stack flex={1}>
            <BybDatePicker
              value={parseDate(details.end_at)}
              onChange={(date: Date | null) => handleDateChange("end_at", date)}
              label="End date"
              renderInput={(params) => (
                <BybTextField
                  sx={{
                    '& .MuiInputBase-root': {
                      paddingRight: theme.spacing(2),
                    }
                  }}
                  {...params}
                  fullWidth
                  variant="filled"
                  placeholder="Select end date"
                  hasValue={!!details.end_at}
                />
              )}
              inputFormat="dd.MM.yyyy"
            />
          </Stack>
        </Stack>

        {/* Description */}
        <Stack>
          <BybTextField
            label="Description"
            fullWidth
            variant="filled"
            multiline
            rows={4}
            placeholder="Enter playlist description"
            value={details.description || ""}
            hasValue={!!details.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </Stack>

        {/* Share */}
        <Stack>
          <BybTextField
            label="Share"
            fullWidth
            variant="filled"
            placeholder="Click to manage sharing"
            value={`Shared with ${details.shared_with_users?.length || 0} Users • ${details.shared_with_teams?.length || 0} Teams${globalState.user?.isSuperAdmin ? ` • ${details.shared_with_accounts?.length || 0} Accounts` : ""}`}
            hasValue={true}
            onClick={() => setSharingDialogOpen(true)}
            sx={{ cursor: 'pointer' }}
          />
        </Stack>

        {/* Visibility */}
        <Stack>
          <BybTextField
            label="Visibility"
            fullWidth
            variant="filled"
            // readOnly
            value={details.published ? "Published - Visible to all it is shared with" : "Draft - Only visible to admins"}
            hasValue={true}
            InputProps={{
              endAdornment: (
                <BybSwitch
                  checked={!!details.published}
                  onChange={(e) => handleChange("published", e.target.checked)}
                  sx={{ ml: 2 }}
                />
              )
            }}
          />
        </Stack>

        <PlaylistSharingDialog
          open={sharingDialogOpen}
          onClose={() => setSharingDialogOpen(false)}
          onSelect={handleSharingSelect}
          initialUsers={getInitialUsers()}
          initialTeams={getInitialTeams()}
          initialAccounts={getInitialAccounts()}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default PlaylistDetails;
